/**
 * CitationTools — admin helper for inserting KB citations into a lesson.
 *
 * Exposes two side-by-side buttons to be mounted at the top of
 * LessonForm's editing surface:
 *
 *   1. "Insert citation"   — opens a KB library browser. Admin searches
 *                             + picks a doc, we copy `[cite:doc-id]` to
 *                             the clipboard so they can paste it into
 *                             any of the mixed editors (RichText, plain
 *                             textarea, section-level markdown) without
 *                             us having to detect focus/cursor.
 *
 *   2. "Suggest citations" — runs the AI suggestion endpoint against
 *                             the lesson body currently on disk, opens
 *                             a modal that lets the admin approve /
 *                             reject each paragraph-level suggestion.
 *                             Approved suggestions are concatenated
 *                             into one clipboard-ready block.
 */
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { AuthContext } from '../../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Search, Copy, Sparkles, X, Check, XCircle, Quote, Upload, FileText, StickyNote, Loader2 } from 'lucide-react';
import { stringifyError } from '../../../utils/errors';

const copyToClipboard = (text) => {
  try {
    navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

// ---- Insert-citation picker ------------------------------------------

const CitationPickerModal = ({ open, onClose }) => {
  const { API } = useContext(AuthContext);
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [counts, setCounts] = useState({ documents: 0, text_entries: 0 });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [loadError, setLoadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await axios.get(`${API}/admin/citations/library`);
      setDocs(data?.items || []);
      setCategories(data?.categories || []);
      setCounts(data?.counts || { documents: 0, text_entries: 0 });
    } catch (err) {
      const status = err?.response?.status;
      // Coerce ANY shape (Pydantic array, dict, string, thrown Error)
      // into a safe string BEFORE it lands in state — the JSX below
      // renders `{loadError}` directly and would otherwise blow up on
      // a raw ValidationError dict with "Objects are not valid as a
      // React child (found: object with keys {type, loc, msg, ...})".
      const detail = stringifyError(err, 'Unknown error');
      const msg = status ? `HTTP ${status} — ${detail}` : detail;
      setLoadError(msg);
      toast.error(`Failed to load Knowledge Library: ${msg}`, { duration: 8000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    load();
    // eslint-disable-next-line
  }, [open, API]);

  // Drag-drop / picker upload — hits the same endpoint the KB Manager
  // uses so the file is ingested, chunked and embedded exactly the same
  // way. On success we refresh the list in place so the admin sees the
  // fresh doc without leaving the modal.
  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('title', file.name);
      await axios.post(`${API}/admin/ai-knowledge/documents`, form);
      toast.success(`Uploaded ${file.name} — ready to cite.`);
      await load();
    } catch (err) {
      const detail = stringifyError(err, 'Upload failed');
      toast.error(`Upload failed: ${detail}`, { duration: 8000 });
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const filtered = useMemo(() => {
    return docs.filter((d) => {
      if (activeCategory && d.category !== activeCategory) return false;
      if (!q) return true;
      const hay = `${d.title || ''} ${d.filename || ''} ${d.category || ''}`.toLowerCase();
      return hay.includes(q.toLowerCase());
    });
  }, [docs, q, activeCategory]);

  const insert = (d) => {
    const marker = `[cite:${d.id}]`;
    if (copyToClipboard(marker)) {
      toast.success(`Copied ${marker} — paste it into any lesson field.`, { duration: 6000 });
    } else {
      toast.info(`Marker: ${marker} — copy this and paste into the lesson.`, { duration: 10000 });
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur"
      onClick={onClose}
      data-testid="citation-picker-modal"
    >
      <div
        className="bg-background border border-border rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Knowledge Library</div>
            <h3 className="text-lg font-bold mt-1 flex items-center gap-2">
              <Quote className="w-5 h-5" /> Insert citation
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Pick a source — we copy the <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">[cite:doc-id]</code> marker to your clipboard. Paste it anywhere in the lesson content.
            </p>
            {(counts.documents + counts.text_entries > 0) && (
              <p className="text-[11px] text-muted-foreground mt-1" data-testid="citation-library-counts">
                <strong>{counts.documents}</strong> uploaded {counts.documents === 1 ? 'document' : 'documents'} · <strong>{counts.text_entries}</strong> manual text {counts.text_entries === 1 ? 'entry' : 'entries'} available
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-foreground" data-testid="citation-picker-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 border-b border-border space-y-2">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search title, filename or category…"
                className="pl-9"
                data-testid="citation-picker-search"
                autoFocus
              />
            </div>
            {/* Category filter — matches the section admins already know
                from Manual Text Entries. Empty option = show all. */}
            {categories.length > 0 && (
              <select
                value={activeCategory}
                onChange={(e) => setActiveCategory(e.target.value)}
                className="text-sm border border-input rounded-md px-2 py-2 bg-background max-w-[220px]"
                data-testid="citation-category-filter"
              >
                <option value="">All categories ({docs.length})</option>
                {categories.map((c) => {
                  const n = docs.filter((d) => d.category === c).length;
                  return <option key={c} value={c}>{c} ({n})</option>;
                })}
              </select>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="shrink-0"
              data-testid="citation-upload-btn"
            >
              {uploading
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Uploading…</>
                : <><Upload className="w-3.5 h-3.5 mr-1" /> Upload</>}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.txt,.md,.docx,.doc"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleFile(f); }}
              data-testid="citation-upload-input"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <p className="text-sm text-slate-500 text-center py-6">Loading…</p>
          ) : loadError ? (
            // Fetch failed — show the raw error inline so the admin can
            // screenshot exactly what went wrong instead of seeing "empty".
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-4 space-y-2 text-sm" data-testid="citation-picker-error">
              <div className="font-bold text-rose-800 dark:text-rose-200">Could not load the Knowledge Library</div>
              <div className="text-xs text-rose-700 dark:text-rose-300 break-words whitespace-pre-wrap font-mono">{loadError}</div>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                If you just deployed, the backend may still be warming up. Try again in a moment, or check the Admin Portal → AI Knowledge tab to confirm documents are visible there.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            // Two empty-state paths: nothing matches the search, OR the
            // library really has zero docs. The second case is what the
            // user hit on production — likely because they uploaded into
            // the older "AI Knowledge (Q&A)" panel instead of the
            // Documents subsection.
            docs.length === 0 ? (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3 text-sm" data-testid="citation-picker-empty">
                <div className="font-bold text-foreground">The Knowledge Library is empty</div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Citations pull from the <strong>Documents</strong> library inside AI Knowledge — not the older Q&amp;A entries. If you uploaded files but they aren&apos;t showing here, they were probably added to the Q&amp;A tab by mistake.
                </p>
                <a
                  href="/admin/portal"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-bold hover:bg-primary/90 transition-colors"
                  data-testid="citation-picker-upload-link"
                >
                  <Upload className="w-3.5 h-3.5" />
                  Upload documents in Admin Portal →
                </a>
                <p className="text-[11px] text-muted-foreground">
                  Opens the Admin Portal in a new tab. Look for the <strong>AI Knowledge → Documents</strong> upload panel.
                </p>
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-6" data-testid="citation-picker-no-match">
                No matching documents. Try a different search term.
              </p>
            )
          ) : (
            filtered.map((d) => {
              const isDoc = d.kind === 'document';
              const KindIcon = isDoc ? FileText : StickyNote;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => insert(d)}
                  className="w-full text-left rounded-lg border border-slate-200 dark:border-slate-700 hover:border-primary hover:bg-primary/5 transition-colors p-3 flex items-center justify-between gap-3"
                  data-testid={`citation-picker-doc-${d.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                          isDoc
                            ? 'bg-sky-500/10 text-sky-700 dark:text-sky-300'
                            : 'bg-amber-500/10 text-amber-800 dark:text-amber-200'
                        }`}
                        title={isDoc ? 'Uploaded document' : 'Manual text entry'}
                        data-testid={`citation-kind-${d.id}`}
                      >
                        <KindIcon className="w-3 h-3" />
                        {isDoc ? 'Doc' : 'Text'}
                      </span>
                      {d.category && (
                        <span
                          className="inline-flex rounded-full bg-primary/10 text-primary text-[10px] px-1.5 py-0.5 font-semibold"
                          data-testid={`citation-category-${d.id}`}
                        >
                          {d.category}
                        </span>
                      )}
                    </div>
                    <p className="font-semibold text-sm truncate">{d.title}</p>
                    <p className="text-[11px] text-slate-500 truncate">
                      {d.filename ? `${d.filename} · ` : ''}{d.chunk_count} {d.chunk_count === 1 ? 'chunk' : 'chunks'}
                    </p>
                  </div>
                  <span className="text-xs text-primary font-semibold whitespace-nowrap flex items-center gap-1 shrink-0">
                    <Copy className="w-3 h-3" /> Copy marker
                  </span>
                </button>
              );
            })
          )}
        </div>
        {/* Drag-drop overlay — visible only while dragging so it doesn't
            occlude the list. Also acts as a persistent hint at the bottom. */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`border-t border-border p-3 text-center text-xs transition-colors ${
            dragOver
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground bg-muted/30'
          }`}
          data-testid="citation-dropzone"
        >
          <Upload className="w-3.5 h-3.5 inline mr-1" />
          {dragOver ? 'Drop the file to upload…' : 'Drop a PDF or .txt here to add a new citation source'}
        </div>
      </div>
    </div>
  );
};

// ---- AI Suggest Modal ------------------------------------------------

const SuggestModal = ({ open, onClose, lessonId, getLiveContent }) => {
  const { API } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [decisions, setDecisions] = useState({});   // paragraph_idx -> 'approved' | 'rejected'

  useEffect(() => {
    if (!open || !lessonId) return;
    (async () => {
      setLoading(true);
      setDecisions({});
      try {
        // Send the CURRENT editor text so we can suggest against unsaved
        // work. Backend falls back to the stored lesson if `content` is
        // empty, so this is safe for both flows.
        const liveContent = typeof getLiveContent === 'function'
          ? (getLiveContent() || '')
          : '';
        const { data } = await axios.post(
          `${API}/admin/lms/lessons/${lessonId}/suggest-citations`,
          { content: liveContent },
        );
        setSuggestions(data || []);
        if (!data || data.length === 0) {
          toast.info(liveContent
            ? 'No paragraphs long enough to match — try adding at least a sentence per paragraph.'
            : 'No lesson text found. Add content to the description or a content section first.');
        }
      } catch (e) {
        toast.error(stringifyError(e, 'Suggest failed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, lessonId, API, getLiveContent]);

  const withSuggestions = suggestions.filter((s) => s.suggestion);
  const approvedRows = withSuggestions.filter((s) => decisions[s.paragraph_idx] === 'approved');

  const setDecision = (idx, v) => setDecisions((d) => ({ ...d, [idx]: v }));

  const copyApproved = () => {
    if (approvedRows.length === 0) {
      toast.info('Approve at least one suggestion first.');
      return;
    }
    // Build a helpful "paragraph → marker" mapping so the admin can
    // quickly see where each marker belongs.
    const lines = approvedRows.map((s) =>
      `▶ Paragraph ${s.paragraph_idx + 1} — "${(s.paragraph || '').slice(0, 80)}${s.paragraph.length > 80 ? '…' : ''}"\n   [cite:${s.suggestion.doc_id}]   (${s.suggestion.source_title})`
    );
    const blob = lines.join('\n\n');
    if (copyToClipboard(blob)) {
      toast.success(`Copied ${approvedRows.length} approved citation${approvedRows.length === 1 ? '' : 's'} to clipboard.`, { duration: 6000 });
    } else {
      toast.error('Clipboard access denied — copy the block manually below.');
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur"
      onClick={onClose}
      data-testid="citation-suggest-modal"
    >
      <div
        className="bg-background border border-border rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">AI Assist</div>
            <h3 className="text-lg font-bold mt-1 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" /> Suggest citations
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              We ran every paragraph through the KB and proposed the single best matching source (if any).
              Approve the ones you want, then copy the block and paste each marker where it belongs.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-foreground" data-testid="citation-suggest-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <p className="text-sm text-slate-500 text-center py-6">Analysing lesson paragraphs…</p>
          ) : suggestions.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">
              No content to analyse yet. Save the lesson first so the AI has text to scan.
            </p>
          ) : withSuggestions.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">
              No paragraphs matched anything in the Knowledge Library — try uploading more source documents first.
            </p>
          ) : (
            withSuggestions.map((s) => {
              const decision = decisions[s.paragraph_idx];
              return (
                <div
                  key={s.paragraph_idx}
                  className={`rounded-lg border p-3 space-y-2 ${
                    decision === 'approved' ? 'border-emerald-400 bg-emerald-50/50 dark:bg-emerald-900/10' :
                    decision === 'rejected' ? 'border-slate-200 bg-slate-100/40 dark:bg-slate-800/40 opacity-60' :
                    'border-slate-200 dark:border-slate-700'
                  }`}
                  data-testid={`citation-suggest-row-${s.paragraph_idx}`}
                >
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    Paragraph {s.paragraph_idx + 1}
                  </div>
                  <p className="text-xs italic text-slate-700 dark:text-slate-300 line-clamp-3">
                    “{s.paragraph}”
                  </p>
                  <div className="rounded bg-slate-50 dark:bg-slate-800 p-2 border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-semibold truncate">{s.suggestion.source_title}</span>
                      <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 px-1.5 py-0.5 rounded whitespace-nowrap">
                        {Math.round(s.suggestion.score * 100)}% match
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-600 dark:text-slate-300 line-clamp-2">{s.suggestion.snippet}</p>
                    <code className="text-[10px] text-slate-500 block mt-1">[cite:{s.suggestion.doc_id}]</code>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant={decision === 'rejected' ? 'secondary' : 'outline'}
                      size="sm"
                      onClick={() => setDecision(s.paragraph_idx, 'rejected')}
                      data-testid={`citation-suggest-reject-${s.paragraph_idx}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                    <Button
                      variant={decision === 'approved' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDecision(s.paragraph_idx, 'approved')}
                      data-testid={`citation-suggest-approve-${s.paragraph_idx}`}
                    >
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {withSuggestions.length > 0 ? (
          <div className="border-t border-border p-4 flex items-center justify-between gap-3">
            <span className="text-xs text-slate-500">
              {approvedRows.length} of {withSuggestions.length} approved
            </span>
            <Button onClick={copyApproved} disabled={approvedRows.length === 0} data-testid="citation-suggest-copy-approved">
              <Copy className="w-4 h-4 mr-2" /> Copy approved as block
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

// ---- Public composite widget -----------------------------------------

export default function CitationTools({ lessonId, getLiveContent }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);

  return (
    <>
      <Card className="border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-900/10">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-sm flex items-center gap-2">
                <Quote className="w-4 h-4 text-amber-600" /> Citation tools
              </CardTitle>
              <p className="text-[11px] text-slate-500 mt-1">
                Ground this lesson in your Knowledge Library. Insert <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">[cite:doc-id]</code> markers manually, or let the AI propose them paragraph-by-paragraph.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPickerOpen(true)}
                data-testid="lesson-form-insert-citation"
              >
                <Quote className="w-4 h-4 mr-2" /> Insert citation
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSuggestOpen(true)}
                disabled={!lessonId}
                data-testid="lesson-form-suggest-citations"
                title={lessonId
                  ? 'Ask the AI to suggest citations — analyses the current draft, no save required'
                  : 'Save the lesson once so it has an ID, then run Suggest on the live draft'}
              >
                <Sparkles className="w-4 h-4 mr-2" /> Suggest citations (AI)
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-[11px] text-slate-500">
            After copying a marker, paste it into any lesson body field (description, notes,
            markdown, section content). Save when done — students will see the
            corresponding <code>[N]</code> superscripts and source cards on the lesson page.
          </div>
        </CardContent>
      </Card>

      <CitationPickerModal open={pickerOpen} onClose={() => setPickerOpen(false)} />
      <SuggestModal
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        lessonId={lessonId}
        getLiveContent={getLiveContent}
      />
    </>
  );
}
